import React, { useMemo, useState } from 'react';
import { Button, OutlineButton } from '@/components/display/button';
import { FileExport } from '@/icons/file-export';
import { Eye } from '@/icons/eye';
import { ExportConfirmationModal } from './export-confirmation-modal';
import { useCampaignContext } from '@/features/campaigns/campaign-context';
import { trpc } from '@/utils/trpc-client';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/forms/ui/popover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTriangleExclamation, faXmark } from '@fortawesome/free-solid-svg-icons';
import { CampaignIO, IOStatus } from 'shared/src/api/io-types';
import { sortIOsByStatus } from '@/features/insertion-orders/io-utils';

const ZOHO_CREATE_IO_URL =
  'https://crm.zoho.com/crm/org643932192/tab/CustomModule19/create?layoutId=2461504000130477043';

const ZOHO_VIEW_IO_URL = 'https://crm.zoho.com/crm/org643932192/tab/CustomModule19';

const STATUS_LABELS: Record<IOStatus, string> = {
  no_doc: 'No ZohoSign Doc',
  created: 'ZohoSign Doc Created',
  recalled: 'Recalled',
  declined: 'Declined',
  expired: 'Expired',
  out_for_signature: 'Out for Signature',
  completed: 'Completed'
};

function isWarningStatus(status: IOStatus): boolean {
  return status === 'out_for_signature' || status === 'completed';
}

export function SyncIOToZohoButton() {
  const { campaign, ios: rawIos, iosLoading, iosError } = useCampaignContext();
  const [isOpen, setIsOpen] = useState(false);
  const [exportingIoId, setExportingIoId] = useState<string | null>(null);
  const [exportedIo, setExportedIo] = useState<CampaignIO | null>(null);
  const [errorIoId, setErrorIoId] = useState<string | null>(null);
  const [confirmModalIo, setConfirmModalIo] = useState<CampaignIO | null>(null);

  const utils = trpc.useUtils();

  const exportMutation = trpc.createProposalIO.useMutation({
    onSuccess: () => {
      utils.campaignInsertionOrders.invalidate();
    }
  });

  const ios = useMemo(() => sortIOsByStatus(rawIos), [rawIos]);
  const iosReady = !iosLoading && !iosError;

  const handleCreateIOClick = () => {
    window.open(ZOHO_CREATE_IO_URL, '_blank');
  };

  const handleExportClick = (io: CampaignIO) => {
    if (isWarningStatus(io.status)) {
      setConfirmModalIo(io);
      return;
    }
    executeExport(io);
  };

  const executeExport = (io: CampaignIO) => {
    setExportingIoId(io.io_id);
    setErrorIoId(null);

    exportMutation.mutate(
      { proposal_id: campaign.proposal_id, zoho_io_id: io.io_id },
      {
        onSuccess: () => {
          setExportingIoId(null);
          setExportedIo(io);
        },
        onError: () => {
          setExportingIoId(null);
          setErrorIoId(io.io_id);
        }
      }
    );
  };

  const handleConfirmExport = () => {
    if (confirmModalIo) {
      executeExport(confirmModalIo);
      setConfirmModalIo(null);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setExportedIo(null);
      setErrorIoId(null);
    }
    setIsOpen(open);
  };

  const handleViewInZoho = () => {
    if (exportedIo) {
      window.open(`${ZOHO_VIEW_IO_URL}/${exportedIo.io_id}`, '_blank');
    }
  };

  return (
    <>
      <ExportConfirmationModal
        io={confirmModalIo}
        onClose={() => setConfirmModalIo(null)}
        onConfirm={handleConfirmExport}
      />
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <div>
            <OutlineButton onClick={() => setIsOpen(!isOpen)} selected={isOpen}>
              <FileExport color={'blue'} />
              <div>Sync IO to Zoho</div>
            </OutlineButton>
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[330px] p-0"
          onInteractOutside={e => {
            if (confirmModalIo) {
              e.preventDefault();
            }
          }}>
          {exportedIo ? (
            <ExportSuccessState
              onClose={() => handleOpenChange(false)}
              onViewInZoho={handleViewInZoho}
            />
          ) : (
            <div className="flex flex-col gap-4 p-4">
              <h3 className="text-base font-semibold text-gray-800">Export IO directly to Zoho</h3>

              {iosLoading && <LoadingState />}
              {iosError && <ErrorState />}
              {iosReady && ios.length === 0 && <EmptyState onCreateClick={handleCreateIOClick} />}
              {iosReady && ios.length > 0 && (
                <LoadedState
                  ios={ios}
                  onCreateClick={handleCreateIOClick}
                  onExportClick={handleExportClick}
                  exportingIoId={exportingIoId}
                  errorIoId={errorIoId}
                />
              )}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}

function LoadingState() {
  return (
    <div className="flex h-[60px] items-center justify-center rounded bg-blue-50">
      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-500" size="lg" />
    </div>
  );
}

function ErrorState() {
  return (
    <div className="flex h-6 items-center overflow-hidden rounded bg-red-600">
      <div className="flex h-full items-center p-1">
        <FontAwesomeIcon icon={faXmark} className="text-white" size="sm" />
      </div>
      <div className="flex h-full flex-1 items-center rounded bg-red-200 px-2">
        <span className="text-sm text-gray-800">
          <span className="font-bold">Network Error:</span> Couldn&apos;t retrieve IOs
        </span>
      </div>
    </div>
  );
}

function ExportErrorState() {
  return (
    <div className="flex h-6 items-center overflow-hidden rounded bg-red-600">
      <div className="flex h-full items-center p-1">
        <FontAwesomeIcon icon={faXmark} className="text-white" size="sm" />
      </div>
      <div className="flex h-full flex-1 items-center rounded bg-red-200 px-2">
        <span className="text-sm text-gray-800">
          <span className="font-bold">Export failed</span>
        </span>
      </div>
    </div>
  );
}

function ExportSuccessState({
  onClose,
  onViewInZoho
}: {
  onClose: () => void;
  onViewInZoho: () => void;
}) {
  return (
    <div className="flex flex-col gap-4 rounded-lg bg-blue-50 px-4 pb-4 pt-2">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-800">IO exported. What&apos;s next?</h3>
        <button
          onClick={onClose}
          className="flex shrink-0 cursor-pointer items-center rounded bg-blue-100 p-[3px] hover:bg-blue-200">
          <FontAwesomeIcon icon={faXmark} className="text-blue-600" size="sm" />
        </button>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="w-3 text-center text-lg font-bold text-blue-700">1</span>
        <span className="text-sm text-gray-800">Go to the IO in Zoho</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="w-3 text-center text-lg font-bold text-blue-700">2</span>
        <span className="text-sm text-gray-800">
          Create and send the ZohoSign Document to the client for signature via Mail Merge.
        </span>
      </div>
      <Button size="compact" className="w-full flex justify-around" onClick={onViewInZoho}>
        <div className="flex gap-2 items-center">
          <Eye className="text-white" />
          <span>View IO in Zoho</span>
        </div>
      </Button>
    </div>
  );
}

function EmptyState({ onCreateClick }: { onCreateClick: () => void }) {
  return (
    <>
      <div className="flex h-6 items-center rounded bg-gray-100 px-2">
        <span className="text-xs text-gray-800">
          <span className="font-bold">0 IOs</span> found for this campaign
        </span>
      </div>
      <div className="flex flex-col items-center gap-1 pt-2">
        <span className="text-xs text-gray-500">Haven&apos;t created the IO yet?</span>
        <Button onClick={onCreateClick} size="compact" className="w-full flex justify-around">
          Create Insertion Order in Zoho
        </Button>
      </div>
    </>
  );
}

type LoadedStateProps = {
  ios: CampaignIO[];
  onCreateClick: () => void;
  onExportClick: (io: CampaignIO) => void;
  exportingIoId: string | null;
  errorIoId: string | null;
};

function LoadedState({
  ios,
  onCreateClick,
  onExportClick,
  exportingIoId,
  errorIoId
}: LoadedStateProps) {
  const isExporting = exportingIoId !== null;

  return (
    <>
      <div className="flex h-6 items-center rounded bg-gray-100 px-2">
        <span className="text-xs text-gray-800">
          <span className="font-bold">
            {ios.length} {ios.length === 1 ? 'IO' : 'IOs'}
          </span>{' '}
          found for this campaign
        </span>
      </div>
      <div className="flex max-h-[160px] flex-col gap-2 overflow-y-auto">
        {ios.map(io => (
          <IORow
            key={io.io_id}
            io={io}
            onExportClick={onExportClick}
            isExporting={exportingIoId === io.io_id}
            hasError={errorIoId === io.io_id}
            disabled={isExporting}
          />
        ))}
      </div>
      <div className="flex flex-col items-start border-t border-gray-300 pt-2">
        <span className="text-xs text-gray-500">Haven&apos;t created the IO yet?</span>
        <button
          onClick={onCreateClick}
          className="cursor-pointer rounded p-1 text-sm text-blue-600 hover:bg-blue-50">
          Create Insertion Order in Zoho
        </button>
      </div>
    </>
  );
}

type IORowProps = {
  io: CampaignIO;
  onExportClick: (io: CampaignIO) => void;
  isExporting: boolean;
  hasError: boolean;
  disabled: boolean;
};

function IORow({ io, onExportClick, isExporting, hasError, disabled }: IORowProps) {
  const showButton = !isExporting && !hasError;

  return (
    <div className="group flex flex-col gap-1 rounded hover:bg-blue-50">
      <div className="relative flex h-6 items-center rounded bg-black/[0.03] pl-1 group-hover:bg-blue-100">
        <span
          className={`min-w-0 truncate text-sm font-medium text-gray-800 group-hover:text-blue-600 ${
            showButton ? 'flex-1 group-hover:mr-[60px]' : hasError ? 'mr-2' : 'flex-1'
          }`}>
          {io.io_name}
        </span>
        {isExporting && (
          <div className="absolute right-0 top-0 flex h-6 w-[60px] items-center justify-center">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-blue-500" size="sm" />
          </div>
        )}
        {hasError && (
          <div className="shrink-0">
            <ExportErrorState />
          </div>
        )}
        {showButton && (
          <Button
            size="compact"
            borderRadius="rounded"
            className="absolute right-0 top-0 h-6 opacity-0 group-hover:opacity-100"
            onClick={() => onExportClick(io)}
            disabled={disabled}>
            Export
          </Button>
        )}
      </div>
      <div className="flex items-center gap-1 px-1 pb-1 text-xs">
        <span className="font-medium text-gray-800 group-hover:text-blue-600">
          {io.io_number ?? 'No IO#'}
        </span>
        <Separator />
        <span className="text-gray-600 group-hover:text-blue-500">
          {io.line_item_count} Line Items
        </span>
        <Separator />
        <StatusTag status={io.status} />
      </div>
    </div>
  );
}

function Separator() {
  return <div className="h-3 w-px bg-gray-400" />;
}

function StatusTag({ status }: { status: IOStatus }) {
  const isWarning = isWarningStatus(status);

  if (isWarning) {
    return (
      <div className="flex items-center overflow-hidden rounded-sm bg-yellow-200 pl-1">
        <span className="text-xs text-gray-800">{STATUS_LABELS[status]}</span>
        <div className="ml-1 flex h-full items-center bg-yellow-300 p-1">
          <FontAwesomeIcon icon={faTriangleExclamation} className="text-gray-800" size="xs" />
        </div>
      </div>
    );
  }

  return (
    <span className="px-1 text-gray-600 group-hover:text-blue-500">{STATUS_LABELS[status]}</span>
  );
}
